<template>
  <div class="pg-sent">
    <!-- 섹션 레벨: 제목 -->
    <div class="p-field">
      <label class="p-field__label">Section Title</label>
      <input
        v-model="sectionData.content.sectionTitle"
        type="text"
        class="p-input"
        placeholder="섹션 제목"
      />
    </div>

    <!-- 문항 탭 -->
    <question-tabs
      :count="questions.length"
      v-model="currentIndex"
      @add="addQuestion"
      @remove="removeQuestion"
    />

    <template v-if="current">
      <div class="p-form-grid p-form-grid--2">
        <div class="p-field">
          <label class="p-field__label">Question / 지시문</label>
          <input
            v-model="current.questionText"
            type="text"
            class="p-input"
            placeholder="예: 음성을 듣고 문장을 완성하세요"
          />
        </div>
        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round pg-sent__audio-ico">music_note</i>
            Audio URL (문항별 문장 음성)
          </label>
          <input
            v-model="current.audioUrl"
            type="text"
            class="p-input"
            placeholder="음성 URL (풀 URL 권장)"
          />
        </div>
      </div>

      <!-- 문장 미리보기 -->
      <div class="pg-sent__preview">
        <span class="pg-sent__preview-label">문장 미리보기</span>
        <div class="pg-sent__preview-row">
          <template v-if="current.tokens.length">
            <span
              v-for="(t, i) in current.tokens"
              :key="i"
              class="pg-sent__preview-cell"
              :class="{ 'is-fixed': t.fixed, 'is-blank': !t.fixed }"
            >{{ t.fixed ? (t.text || "·") : (t.text ? "［ ］" : "□") }}</span>
          </template>
          <span v-else class="pg-sent__preview-empty">단어를 추가하세요</span>
        </div>
      </div>

      <!-- 단어(토큰) -->
      <div class="pg-sent__block">
        <div class="pg-sent__block-head">
          단어 (정답 순서대로)
          <span class="pg-sent__block-sub">고정=미리 표시 / 빈칸=학생이 채움(이 text가 정답)</span>
        </div>
        <div class="pg-sent__list">
          <div
            v-for="(token, index) in current.tokens"
            :key="index"
            class="pg-sent__item"
            :class="{ 'is-fixed': token.fixed }"
          >
            <span class="pg-sent__idx">{{ index + 1 }}</span>
            <input
              v-model="token.text"
              type="text"
              class="p-input pg-sent__item-input"
              placeholder="단어"
            />
            <label class="pg-sent__toggle">
              <input type="checkbox" v-model="token.fixed" />
              <span>고정</span>
            </label>
            <div class="pg-sent__item-actions">
              <button
                type="button"
                class="pg-sent__icon-btn"
                title="앞으로 이동"
                :disabled="index === 0"
                @click.prevent="moveLeft(index)"
              >
                <i class="material-icons-round">arrow_back</i>
              </button>
              <button
                type="button"
                class="pg-sent__icon-btn"
                title="뒤로 이동"
                :disabled="index === current.tokens.length - 1"
                @click.prevent="moveRight(index)"
              >
                <i class="material-icons-round">arrow_forward</i>
              </button>
              <button
                type="button"
                class="pg-sent__icon-btn pg-sent__icon-btn--danger"
                title="제거"
                @click.prevent="removeToken(index)"
              >
                <i class="material-icons-round">delete</i>
              </button>
            </div>
          </div>
        </div>
        <div class="pg-sent__actions">
          <ABtn kind="yellow" icon="add" @click.prevent="addToken">단어 추가</ABtn>
        </div>
      </div>

      <!-- 오답 단어 -->
      <div class="pg-sent__block">
        <div class="pg-sent__block-head">
          오답 단어 (distractors)
          <span class="pg-sent__block-sub">선택. 단어 은행에 섞일 오답만 입력(정답 단어는 위에 내장)</span>
        </div>
        <div class="pg-sent__chips">
          <div
            v-for="(d, index) in current.distractors"
            :key="index"
            class="pg-sent__chip"
          >
            <input
              v-model="current.distractors[index]"
              type="text"
              class="p-input pg-sent__chip-input"
              placeholder="오답 단어"
            />
            <button
              type="button"
              class="pg-sent__chip-x"
              title="제거"
              @click.prevent="removeDistractor(index)"
            >
              <i class="material-icons-round">close</i>
            </button>
          </div>
        </div>
        <div class="pg-sent__actions">
          <ABtn kind="ghost" icon="add" @click.prevent="addDistractor">오답 단어 추가</ABtn>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import ABtn from "@/components/Pineapple/ABtn.vue";
import QuestionTabs from "@/components/Practice/QuestionTabs.vue";
import { useQuestions } from "@/components/Practice/useQuestions";

const sectionData = defineModel({ type: Object, required: true });

const makeEmpty = () => ({
  questionText: "",
  audioUrl: "",
  tokens: [{ text: "", fixed: false }],
  distractors: []
});

const { questions, currentIndex, current, addQuestion, removeQuestion } = useQuestions(
  sectionData,
  makeEmpty,
  ["questionText", "audioUrl", "tokens", "distractors"]
);

const addToken = () => {
  current.value.tokens.push({ text: "", fixed: false });
};

const removeToken = (index) => {
  current.value.tokens.splice(index, 1);
};

const moveLeft = (index) => {
  if (index === 0) return;
  const t = current.value.tokens;
  const tmp = t[index];
  t[index] = t[index - 1];
  t[index - 1] = tmp;
};

const moveRight = (index) => {
  const t = current.value.tokens;
  if (index === t.length - 1) return;
  const tmp = t[index];
  t[index] = t[index + 1];
  t[index + 1] = tmp;
};

const addDistractor = () => {
  current.value.distractors.push("");
};

const removeDistractor = (index) => {
  current.value.distractors.splice(index, 1);
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.pg-sent {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__audio-ico {
    font-size: 14px !important;
    color: $p-point-deep;
    vertical-align: middle;
    margin-right: 4px;
  }

  // ── 문장 미리보기 ──
  &__preview {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px 16px;
    border: 1px dashed $p-line-hard;
    border-radius: $p-radius-md;
    background: $p-bg;
  }

  &__preview-label {
    font-size: 10.5px;
    font-weight: 800;
    letter-spacing: 0.05em;
    color: $p-ink-soft;
    text-transform: uppercase;
    flex-shrink: 0;
  }

  &__preview-row {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    align-items: center;
  }

  &__preview-cell {
    min-width: 36px;
    height: 36px;
    padding: 0 12px;
    display: inline-grid;
    place-items: center;
    border-radius: $p-radius-sm;
    font-size: 15px;
    font-weight: 700;

    &.is-fixed {
      border: 1.5px solid $p-line-hard;
      background: $p-panel-alt;
      color: $p-ink-mid;
    }

    &.is-blank {
      border: 1.5px dashed $p-point-deep;
      background: $p-point-soft;
      color: $p-point-deep;
    }
  }

  &__preview-empty {
    font-size: 13px;
    color: $p-ink-soft;
  }

  // ── 블록 ──
  &__block {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 14px 16px;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    background: $p-panel;
  }

  &__block-head {
    font-size: 13px;
    font-weight: 800;
    color: $p-ink;
    display: flex;
    align-items: baseline;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__block-sub {
    font-size: 11.5px;
    font-weight: 500;
    color: $p-ink-soft;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    border: 1px solid $p-line;
    border-radius: $p-radius-sm;
    background: $p-bg;

    &.is-fixed {
      background: $p-panel-alt;
    }
  }

  &__idx {
    min-width: 24px;
    height: 24px;
    display: inline-grid;
    place-items: center;
    border-radius: 999px;
    background: $p-ink;
    color: $p-point;
    font-family: $p-font-mono;
    font-size: 11px;
    font-weight: 800;
    flex-shrink: 0;
  }

  &__item-input {
    flex: 1;
    min-width: 0;
  }

  &__toggle {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    font-weight: 700;
    color: $p-ink-mid;
    cursor: pointer;
    user-select: none;
    white-space: nowrap;
  }

  &__item-actions {
    display: flex;
    gap: 4px;
    flex-shrink: 0;
  }

  &__icon-btn {
    width: 30px;
    height: 30px;
    border: 0;
    background: transparent;
    color: $p-ink-mid;
    border-radius: $p-radius-sm;
    display: grid;
    place-items: center;
    cursor: pointer;
    transition: background $p-dur $p-ease, color $p-dur $p-ease;

    > i { font-size: 16px; }

    &:hover:not(:disabled) { background: $p-panel-alt; color: $p-ink; }

    &:disabled { opacity: 0.3; cursor: not-allowed; }

    &--danger {
      color: $p-err;
      &:hover:not(:disabled) { background: $p-err-soft; }
    }
  }

  // ── 오답 칩 ──
  &__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  &__chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  &__chip-input {
    max-width: 130px;
  }

  &__chip-x {
    width: 26px;
    height: 26px;
    border: 0;
    background: transparent;
    color: $p-ink-mid;
    border-radius: $p-radius-sm;
    display: grid;
    place-items: center;
    cursor: pointer;

    > i { font-size: 15px; }

    &:hover { background: $p-err-soft; color: $p-err; }
  }

  &__actions {
    display: flex;
    gap: 8px;
  }
}
</style>
